import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy,
  Crown,
  Medal,
  Award,
  Search,
  Loader2,
  RefreshCcw,
  Tablet,
  Edit3,
  Save,
  X,
  Plus,
  Minus,
  Package,
  ShoppingCart,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US");

// Decorations for the top 3 podium spots
const RANK_DECOR = {
  1: { icon: Crown, color: "from-yellow-400 via-amber-500 to-orange-500", text: "text-amber-900", label: "🥇 الأول", bg: "bg-yellow-50", border: "border-yellow-400" },
  2: { icon: Medal, color: "from-slate-300 via-slate-400 to-slate-500", text: "text-slate-800", label: "🥈 الثاني", bg: "bg-slate-50", border: "border-slate-400" },
  3: { icon: Award, color: "from-amber-600 via-orange-700 to-amber-900", text: "text-orange-900", label: "🥉 الثالث", bg: "bg-orange-50", border: "border-orange-500" },
};

export default function MarketRankingTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [savingId, setSavingId] = useState(null);
  const [onlyWithOrders, setOnlyWithOrders] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      const { data } = await axios.get(`${API}/admin/market-ranking`, { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل تحميل الترتيب");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditValue(String(m.manual_adjustment ?? 0));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const saveEdit = async (m) => {
    const v = parseInt(editValue, 10);
    if (Number.isNaN(v)) {
      toast.error("أدخل رقماً صحيحاً (يمكن أن يكون سالب)");
      return;
    }
    if (v < -1_000_000 || v > 1_000_000) {
      toast.error("القيمة خارج الحدود");
      return;
    }
    setSavingId(m.id);
    try {
      await axios.patch(`${API}/admin/market-ranking/${m.id}`, { manual_adjustment: v });
      toast.success(
        v === 0
          ? `تم إلغاء التعديل اليدوي على «${m.market_name}»`
          : `تم تعديل كراتين «${m.market_name}» بقيمة ${v >= 0 ? "+" : ""}${v}`,
      );
      setEditingId(null);
      setEditValue("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "فشل الحفظ");
    } finally {
      setSavingId(null);
    }
  };

  // Quick step buttons for inline edit (±1, ±10)
  const stepEdit = (delta) => {
    const cur = parseInt(editValue || "0", 10);
    const next = (Number.isNaN(cur) ? 0 : cur) + delta;
    setEditValue(String(next));
  };

  const totals = data?.totals || { count: 0, with_orders: 0, total_cartons: 0 };
  const markets = (data?.markets || []).filter(
    (m) => !onlyWithOrders || m.computed_orders > 0 || m.manual_adjustment !== 0,
  );

  return (
    <div className="space-y-4" dir="rtl" data-testid="market-ranking-tab">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-bl from-[#7c2d12] via-[#b45309] to-[#92400e] text-white p-5 shadow-2xl">
        <div className="absolute -top-10 -left-10 w-44 h-44 bg-yellow-300/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-10 -right-10 w-44 h-44 bg-amber-200/15 rounded-full blur-3xl pointer-events-none" />
        <div className="relative flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-yellow-200/90 text-xs font-bold tracking-wider uppercase">
              <Trophy className="w-4 h-4" />
              ترتيب الماركتات
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold mt-1">
              ⚔️ أكثر الماركتات طلباً
            </h1>
            <p className="text-white/85 text-xs mt-2 max-w-xl leading-relaxed">
              الترتيب تلقائي حسب الكراتين (المعتمدة + التعديل اليدوي). إذا الأرقام مو دقيقة، استخدم التعديل اليدوي لأي ماركت تصلّح وضعه.
            </p>
          </div>
          <button
            data-testid="ranking-refresh-btn"
            onClick={load}
            className="bg-white/15 hover:bg-white/25 backdrop-blur px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1"
          >
            <RefreshCcw className="w-3.5 h-3.5" />
            تحديث
          </button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-slate-200">
          <div className="text-[11px] text-slate-500 font-bold">إجمالي الماركتات</div>
          <div className="font-extrabold text-2xl text-[#1E3A5F] tabular-nums">{fmt(totals.count)}</div>
        </div>
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-emerald-200">
          <div className="text-[11px] text-emerald-700 font-bold">مع طلبات</div>
          <div className="font-extrabold text-2xl text-emerald-700 tabular-nums">{fmt(totals.with_orders)}</div>
        </div>
        <div className="bg-white rounded-2xl p-3 shadow-sm border border-amber-300">
          <div className="text-[11px] text-amber-700 font-bold">إجمالي الكراتين</div>
          <div className="font-extrabold text-2xl text-amber-700 tabular-nums">{fmt(totals.total_cartons)}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="ranking-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث باسم الماركت / الهاتف / رقم الايباد"
            className="w-full bg-white border-2 border-slate-200 rounded-xl pr-10 pl-3 py-2.5 text-sm focus:border-amber-500 outline-none"
          />
        </div>
        <label className="flex items-center gap-1.5 bg-white border-2 border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-700 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyWithOrders}
            onChange={(e) => setOnlyWithOrders(e.target.checked)}
            data-testid="ranking-only-with-orders"
          />
          فقط مع طلبات
        </label>
      </div>

      {/* List */}
      {loading && !data ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
        </div>
      ) : markets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-2xl border border-slate-200">
          <Trophy className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500 text-sm">لا توجد ماركتات مطابقة</p>
        </div>
      ) : (
        <div className="space-y-2">
          {markets.map((m) => {
            const decor = RANK_DECOR[m.rank];
            const isEditing = editingId === m.id;
            const isSaving = savingId === m.id;
            const isPodium = m.rank <= 3;

            return (
              <div
                key={m.id}
                data-testid={`market-row-${m.id}`}
                className={`bg-white rounded-2xl shadow-sm border-2 p-3 transition ${
                  isPodium ? decor.border : "border-slate-200"
                } ${isPodium ? decor.bg : ""}`}
              >
                <div className="flex items-center gap-3">
                  {/* Rank badge */}
                  <div
                    className={`flex-shrink-0 w-14 h-14 rounded-2xl flex flex-col items-center justify-center font-extrabold shadow-md ${
                      isPodium
                        ? `bg-gradient-to-br ${decor.color} text-white border-2 ${decor.border}`
                        : "bg-slate-100 text-slate-700 border-2 border-slate-200"
                    }`}
                  >
                    {isPodium ? (
                      <>
                        {React.createElement(decor.icon, { className: "w-4 h-4 mb-0.5 text-white drop-shadow" })}
                        <span className="text-base leading-none">#{m.rank}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-[10px] opacity-60">رتبة</span>
                        <span className="text-lg leading-none">#{m.rank}</span>
                      </>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className={`font-bold text-sm ${isPodium ? decor.text : "text-slate-900"} truncate`}>
                        {isPodium && <span className="ml-1">{decor.label.split(" ")[0]}</span>}
                        {m.market_name}
                      </h3>
                      {m.customer_id && (
                        <span className="bg-[#3B82F6]/15 text-[#1E3A5F] text-[9px] font-bold rounded-full px-1.5 py-0.5 flex items-center gap-0.5">
                          <Tablet className="w-2.5 h-2.5" />
                          {m.customer_id}
                        </span>
                      )}
                      {/* Tier badge: numbered circle instead of A+/B+/C+
                          letters — admin requested cleaner visual.
                          A+ → 1 (gold), B+ → 2 (silver), C+ → 3 (bronze) */}
                      {(() => {
                        const tier = m.tier || "A_plus";
                        const styles = {
                          A_plus: { num: 1, bg: "bg-gradient-to-br from-amber-400 to-amber-600", ring: "ring-amber-300" },
                          B_plus: { num: 2, bg: "bg-gradient-to-br from-slate-300 to-slate-500", ring: "ring-slate-300" },
                          C_plus: { num: 3, bg: "bg-gradient-to-br from-orange-400 to-orange-700", ring: "ring-orange-300" },
                        }[tier] || { num: "?", bg: "bg-slate-200", ring: "ring-slate-200" };
                        return (
                          <span
                            data-testid={`tier-circle-${tier}`}
                            title={tier.replace("_plus", "+")}
                            className={`${styles.bg} text-white text-[10px] font-black w-5 h-5 rounded-full flex items-center justify-center ring-2 ${styles.ring}`}
                          >
                            {styles.num}
                          </span>
                        );
                      })()}
                    </div>
                    {m.customer_name && m.customer_name !== m.market_name && (
                      <div className="text-[11px] text-slate-500 truncate">{m.customer_name}</div>
                    )}
                    <div className="text-[11px] text-slate-400" dir="ltr">{m.phone || "—"}</div>
                  </div>

                  {/* Totals (read-only display) */}
                  <div className="flex-shrink-0 text-left">
                    <div className="text-[9px] text-slate-500 font-bold flex items-center justify-end gap-0.5">
                      <Package className="w-2.5 h-2.5" /> الكراتين
                    </div>
                    <div className={`text-xl font-black tabular-nums ${isPodium ? decor.text : "text-[#1E3A5F]"}`} dir="ltr">
                      {fmt(m.total_cartons)}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center justify-end gap-1">
                      <span className="flex items-center gap-0.5">
                        <ShoppingCart className="w-2.5 h-2.5" />
                        {m.computed_orders} طلب
                      </span>
                    </div>
                  </div>
                </div>

                {/* Manual adjustment row */}
                <div className="mt-2 pt-2 border-t border-dashed border-slate-200 flex items-center justify-between gap-2 text-xs">
                  <div className="flex items-center gap-2 text-slate-600">
                    <span>محسوب: <b className="text-slate-800 tabular-nums" dir="ltr">{fmt(m.computed_cartons)}</b></span>
                    <span className="text-slate-300">+</span>
                    <span>
                      تعديل يدوي:{" "}
                      {isEditing ? null : (
                        <b
                          className={`tabular-nums ${
                            m.manual_adjustment > 0
                              ? "text-emerald-700"
                              : m.manual_adjustment < 0
                                ? "text-rose-700"
                                : "text-slate-400"
                          }`}
                          dir="ltr"
                        >
                          {m.manual_adjustment > 0 ? "+" : ""}
                          {fmt(m.manual_adjustment)}
                        </b>
                      )}
                    </span>
                  </div>

                  {isEditing ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => stepEdit(-1)}
                        className="w-7 h-7 bg-rose-100 text-rose-700 rounded-lg hover:bg-rose-200 flex items-center justify-center"
                        title="-1"
                        data-testid={`step-minus-${m.id}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <input
                        data-testid={`adjustment-input-${m.id}`}
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-20 text-center border-2 border-amber-400 rounded-lg px-1 py-1 text-sm font-bold tabular-nums"
                        dir="ltr"
                      />
                      <button
                        type="button"
                        onClick={() => stepEdit(1)}
                        className="w-7 h-7 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 flex items-center justify-center"
                        title="+1"
                        data-testid={`step-plus-${m.id}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => saveEdit(m)}
                        disabled={isSaving}
                        data-testid={`save-adjustment-${m.id}`}
                        className="bg-emerald-600 text-white rounded-lg px-2 py-1 text-xs font-bold flex items-center gap-1 disabled:opacity-50"
                      >
                        {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        حفظ
                      </button>
                      <button
                        onClick={cancelEdit}
                        disabled={isSaving}
                        className="bg-slate-100 text-slate-600 rounded-lg px-2 py-1 text-xs font-bold flex items-center gap-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEdit(m)}
                      data-testid={`edit-adjustment-${m.id}`}
                      className="bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-lg px-2 py-1 text-xs font-bold flex items-center gap-1 transition"
                    >
                      <Edit3 className="w-3 h-3" />
                      تعديل الكميات
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Hint card */}
      <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-3 text-xs text-amber-900 leading-relaxed mt-4">
        <div className="font-bold mb-1 flex items-center gap-1">
          <Edit3 className="w-3.5 h-3.5" />
          ملاحظة:
        </div>
        قيمة &quot;محسوب&quot; تأتي تلقائياً من فواتير الزبون المعتمدة. أما &quot;التعديل اليدوي&quot; فهو قيمة تضيفها أنت (موجبة أو سالبة) لتصحيح أي خلل بالأرقام التاريخية أو رفع/خفض ترتيب ماركت معيّن. الرقم النهائي = محسوب + تعديل، وعلى أساسه يطلع الترتيب.
      </div>
    </div>
  );
}
